import Uppy, { Body, Meta } from '@uppy/core';
import Dashboard, { DashboardOptions } from '@uppy/dashboard';
import Dropbox from '@uppy/dropbox';
import Transloadit, { COMPANION_URL, COMPANION_ALLOWED_HOSTS } from '@uppy/transloadit';
import type { AssemblyOptions } from '@uppy/transloadit';
import type { Locale } from '@uppy/utils/lib/Translator';
import { FunctionComponent, useEffect, useRef } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { sendAction } from '@helpers/datadog-rum';
import { TransloaditTemplateMap } from '@helpers/uppy';
import { type UppyUploaderProps } from '@components/common/uppy/types/UppyTypes';
import { finishUploadSession, hasUploadsInProgress } from '@components/common/uppy/uploadSession';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';

import './styles/uppy_uploader.scss';

interface TransloaditCreds { params: string; signature: string }

// Uppy's PluginTarget type does not accept a generic plugin class directly, but the
// runtime value is the Dashboard plugin, which is what `.use({ target })` expects.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DASHBOARD_TARGET = Dashboard as any;

export const UppyUploader: FunctionComponent<UppyUploaderProps> = ({
  handleUpload,
  uniqIdentifier,
  button = false,
  closeOnComplete = false,
  locale = {
    strings: {
      dropPaste: 'Drag file(s) here or %{browse}',
      dropHereOr: 'Drag file(s) here or %{browse}',
      browse: 'click to browse'
    }
  },
  restrictions = {},
  template = 'file_ingest',
  trigger = undefined,
  uploadLinkKey
}) => {
  // One instance per mount. `restrictions`/`locale` arrive as inline object
  // literals, so using them as `useMemo` deps rebuilt Uppy on every parent
  // render and left the replacement without the plugins the mount effect
  // installed on the original instance.
  const uppyRef = useRef<Uppy<Meta, Body> | null>(null);
  if (uppyRef.current === null) {
    uppyRef.current = new Uppy<Meta, Body>({
      allowMultipleUploadBatches: true,
      id: `${uniqIdentifier}-uppy`,
      restrictions: { maxNumberOfFiles: 3000, ...restrictions },
      locale: locale as unknown as Locale,
    });
  }
  const uppy = uppyRef.current;

  // Keep the newest callback reachable from the mount-only effect below.
  const handleUploadRef = useRef(handleUpload);
  handleUploadRef.current = handleUpload;

  // `closeOnComplete` tears the instance down early; make sure unmount does not
  // destroy it a second time.
  const isDestroyedRef = useRef(false);
  const destroyUppy = (): void => {
    if (isDestroyedRef.current) return;
    isDestroyedRef.current = true;
    uppy.destroy();
  };

  const restrictionsKey = JSON.stringify(restrictions);
  const localeKey = JSON.stringify(locale);
  useEffect(() => {
    uppy.setOptions({
      restrictions: { maxNumberOfFiles: 3000, ...restrictions },
      locale: locale as unknown as Locale,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictionsKey, localeKey]);

  const templateName = template.replace(/-/g, '_');
  const dashboardOptions: DashboardOptions<Meta, Body> = {
    browserBackButtonClose: button,
    // Dashboard would close after the first finished batch; `finishUploadSession` closes it once all are done
    closeAfterFinish: false,
    closeModalOnClickOutside: button,
    inline: false,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: true,
    trigger,
    // eslint-disable-next-line
    // @ts-ignore
    width: 10
  };

  const handleError = (file, error): void => {
    sendAction('Error Uploading File to Transloadit', {
      tags: {},
      extra: { file, error }
    });
  };

  const templateId = TransloaditTemplateMap()[templateName]
    || TransloaditTemplateMap().file_ingest;

  const credsRef = useRef<Promise<TransloaditCreds> | null>(null);
  const getCreds = (): Promise<TransloaditCreds> => {
    if (!credsRef.current) {
      credsRef.current = fetchJson<{ data: TransloaditCreds }>({
        url: '/api/v4/private/transloadit_creds',
        method: 'POST',
        body: { template_id: templateId, ...uploadLinkKey && { upload_key: uploadLinkKey } }
      }).then((response) => response.data).catch((error) => {
        credsRef.current = null;
        throw error;
      });
    }
    return credsRef.current;
  };

  useEffect(() => {
    // Transloadit awaits `assemblyOptions` outside its own error handling, so a creds failure there marks no file
    // as failed: Retry then finds nothing to retry, and its `complete` clears every file. Fetch the creds in a
    // preprocessor registered ahead of Transloadit's, which knows the batch's files and can fail them.
    const ensureCreds = async (fileIDs: string[]): Promise<void> => {
      try {
        await getCreds();
      } catch (error) {
        fileIDs.forEach((fileID) => {
          uppy.emit('upload-error', uppy.getFile(fileID), error as Error);
        });
        throw error;
      }
    };
    uppy.addPreProcessor(ensureCreds);

    uppy
      .use(Transloadit, {
        assemblyOptions: async (): Promise<AssemblyOptions> => {
          const { params, signature } = await getCreds();
          return { params, signature } as unknown as AssemblyOptions;
        },
        waitForEncoding: true, // ensures request has processed with metadata
      })
      .on('upload-error', (file, body) => {
        handleError(file, body);
      })
      .on('transloadit:complete', (assembly) => {
        const files =
          assembly.results[':original'] || assembly.results[templateName];
        handleUploadRef.current(files);
      })
      .on('complete', () => {
        credsRef.current = null;
        // Reset here rather than in `transloadit:complete`: that fires while its own batch is still in
        // `currentUploads`, so a batch added mid-upload can't be told apart and would be wiped. If
        // `handleUpload` unmounted this component, the unmount cleanup has already destroyed the instance.
        if (isDestroyedRef.current || hasUploadsInProgress(uppy)) return;

        finishUploadSession(uppy, { closeModal: button, destroy: closeOnComplete ? destroyUppy : undefined });
      })
      .use(Dashboard, dashboardOptions)
      .use(Dropbox, {
        companionUrl: COMPANION_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: DASHBOARD_TARGET,
      });

    return (): void => {
      uppy.removePreProcessor(ensureCreds);
      // The Dashboard mounts into `document.body`, so without destroy() the old
      // dashboard (and its file list) survives unmount and then shows up
      // alongside the next one on remount.
      destroyUppy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDestroyedRef.current) return;
    const dashboard = uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard');
    if (dashboard) {
      // Kept in an effect so it cannot run during render (or after teardown).
      dashboard.render(uppy.getState());
    }
  });

  return null;
};
